import React, { useState } from "react";
import styled from "styled-components";
import { colors } from "../../../../styles/style";
import { EditModal } from "../../../shared/EditModal";
import { SmallTextConstant, SmallestText } from "../../../shared/SharedStyles";
import { SharedButton } from "../../../settings/common/shared/SharedButton";
import penIcon from "../../../../images/penIconBlack.svg";
import { deleteFirstWord } from "../../../../api";
import { FirstWord } from "../../../../interface/FirstWordInterface";
import {
  formatTimestampToDefaultDate,
  getApiErrorStatus,
} from "../../../../common/helpers";
import {
  FirstWordScriptText,
  FirstWordSheetHeader,
  HTTP_STATUS_NOT_FOUND,
  SheetErrorText,
  SoundedLikeText,
  firstWordErrorMessages,
  formatAgeOldAt,
  formatSoundedLike,
} from "./FirstWordsShared";
import { useCanEdit } from "../../../../common/useCanEdit";

interface FirstWordDetailsModalProps {
  word: FirstWord;
  dateOfBirth?: number;
  onClose: () => void;
  onEdit: () => void;
  onRemoved: () => void;
}

export const FirstWordDetailsModal: React.FC<FirstWordDetailsModalProps> = ({
  word,
  dateOfBirth,
  onClose,
  onEdit,
  onRemoved,
}) => {
  const canEdit = useCanEdit("first_words");
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // A second tap confirms; window.confirm can block inside the native WebView.
  const handleRemove = async () => {
    if (!isConfirmingRemove) {
      setIsConfirmingRemove(true);
      return;
    }
    setRemoving(true);
    setErrorMessage("");
    try {
      await deleteFirstWord(word.baby_id, word.id);
      onRemoved();
    } catch (error) {
      if (getApiErrorStatus(error) === HTTP_STATUS_NOT_FOUND) {
        onRemoved();
        return;
      }
      console.error("Error removing first word", error);
      setErrorMessage(firstWordErrorMessages.generic);
      setIsConfirmingRemove(false);
      setRemoving(false);
    }
  };

  return (
    <EditModal isOpen={true} onClose={onClose}>
      <FirstWordSheetHeader title="Word details" onClose={onClose} />
      <WordSummary>
        <FirstWordScriptText>{word.word}</FirstWordScriptText>
        {word.sounded_like && (
          <SoundedLikeText>
            {formatSoundedLike(word.sounded_like)}
          </SoundedLikeText>
        )}
      </WordSummary>
      <InfoRows>
        <InfoRow>
          <InfoLabel>Said on</InfoLabel>
          <InfoValue>{formatTimestampToDefaultDate(word.said_on)}</InfoValue>
        </InfoRow>
        {dateOfBirth !== undefined && (
          <InfoRow>
            <InfoLabel>Age</InfoLabel>
            <InfoValue>{formatAgeOldAt(dateOfBirth, word.said_on)}</InfoValue>
          </InfoRow>
        )}
        {word.heard_by && (
          <InfoRow>
            <InfoLabel>Heard by</InfoLabel>
            <InfoValue>{word.heard_by}</InfoValue>
          </InfoRow>
        )}
      </InfoRows>
      <Actions>
        {errorMessage && (
          <SheetErrorText role="alert">{errorMessage}</SheetErrorText>
        )}
        <SharedButton
          iconSrc={penIcon}
          buttonText="Edit"
          onClick={onEdit}
          $disabled={!canEdit || removing}
        />
        <SharedButton
          icon={<TrashIcon />}
          buttonText={
            isConfirmingRemove ? "Tap again to remove" : "Remove word"
          }
          onClick={handleRemove}
          loading={removing}
          $disabled={removing}
          $color={colors.softOrange}
          $borderColor={colors.softOrange}
        />
      </Actions>
    </EditModal>
  );
};

const TrashIcon = () => (
  <svg
    width="19"
    height="19"
    viewBox="0 0 24 24"
    fill="none"
    stroke={colors.softOrange}
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 7h16" />
    <path d="M9.5 7V4.75h5V7" />
    <path d="M6 7l.9 12.1A2 2 0 0 0 8.9 21h6.2a2 2 0 0 0 2-1.9L18 7" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
);

const WordSummary = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin-top: 8px;
  text-align: center;
`;

const InfoRows = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  padding: 0 12px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  min-height: 48px;
  border-bottom: 1px solid ${colors.borderWhite};

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  ${SmallestText}
  color: ${colors.softBrown};
  white-space: nowrap;
`;

const InfoValue = styled.span`
  ${SmallTextConstant}
  color: ${colors.darkBrown};
  text-align: right;
  overflow-wrap: anywhere;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
  padding: 0 12px 8px;
`;
